/**
 * app/(brand)/wl-signup/success/page.js — Phase 8 · 07/2026
 * =========================================================
 * Post-Stripe-Checkout landing page for kalkiai.trade marketing
 * buyers.  When Stripe completes the payment it redirects here
 * with ?session_id=cs_test_... in the URL.  We poll our own
 * status endpoint (POST /api/wl/public-checkout/status) until
 * the checkout.session.completed webhook has finished
 * provisioning the tenant + wl_admin user + credentials email.
 *
 * The user never sees credentials on this page — those go via
 * Resend to the buyer's email.  This page shows a big green
 * confirmation with a "Continue to sign in" button once the
 * tenant slug is known.
 */

import { Suspense, useEffect, useState } from 'react'
import { useSearchParams } from '@/shims/NextNavigation'
import { CheckCircle2, Loader2, Mail, ArrowRight, AlertTriangle } from 'lucide-react'
import SiteFrame from '@/components/landing/SiteFrame'
import { Container, GradientButton, GhostButton, GlassCard } from '@/components/landing/design-system'

function SuccessPageInner() {
  const params = useSearchParams()
  const sessionId = params.get('session_id')

  const [status, setStatus] = useState('polling')  // polling | ready | timeout | error
  const [row, setRow] = useState(null)

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return }
    let cancelled = false
    let attempts = 0
    const MAX_ATTEMPTS = 30  // ~60s at 2s interval

    const poll = async () => {
      attempts += 1
      try {
        const r = await fetch('/api/wl/public-checkout/status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
        })
        const j = await r.json()
        if (cancelled) return
        if (r.ok && j?.status === 'completed') {
          setRow(j); setStatus('ready'); return
        }
        if (r.ok && j?.status === 'pending' && attempts < MAX_ATTEMPTS) {
          setTimeout(poll, 2000); return
        }
        if (attempts >= MAX_ATTEMPTS) { setStatus('timeout'); return }
        // Otherwise keep polling
        setTimeout(poll, 2000)
      } catch {
        if (!cancelled && attempts < MAX_ATTEMPTS) setTimeout(poll, 2000)
        else setStatus('error')
      }
    }
    poll()
    return () => { cancelled = true }
  }, [sessionId])

  const masterDomain = 'kalkiai.trade'
  const loginUrl = row?.tenant_slug ? `https://${row.tenant_slug}.${masterDomain}/login` : null

  return (
    <SiteFrame>
      <section className="relative pt-32 md:pt-40 pb-24 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10 opacity-60"
             style={{ background: 'radial-gradient(55% 45% at 50% 0%, rgba(52,211,153,0.15), transparent 70%)' }} />
        <Container size="narrow">
          <GlassCard className="p-10 md:p-14 text-center space-y-6">
            {status === 'polling' && (
              <>
                <div className="mx-auto h-16 w-16 rounded-full border-2 border-emerald-400/30 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
                </div>
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-400 mb-3">Payment received</div>
                  <h1 className="text-3xl md:text-4xl font-semibold text-white leading-tight">Provisioning your workspace…</h1>
                  <p className="text-zinc-400 md:text-lg leading-relaxed mt-4 max-w-lg mx-auto">
                    Stripe confirmed your payment. We&apos;re spinning up your dedicated tenant,
                    creating your admin sign-in, and sending your credentials by email. This
                    typically takes 10&ndash;30 seconds.
                  </p>
                </div>
              </>
            )}

            {status === 'ready' && (
              <>
                <div className="mx-auto h-16 w-16 rounded-full bg-emerald-400/20 border-2 border-emerald-400/40 flex items-center justify-center">
                  <CheckCircle2 className="h-9 w-9 text-emerald-400" />
                </div>
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-400 mb-3">Workspace live</div>
                  <h1 className="text-3xl md:text-4xl font-semibold text-white leading-tight">Welcome to Kalki Ai Cloud.</h1>
                  <p className="text-zinc-400 md:text-lg leading-relaxed mt-4 max-w-lg mx-auto">
                    Your <b className="text-white">{row?.plan?.toUpperCase()}</b> licence is active. Sign-in credentials
                    were just emailed to <b className="text-white">{row?.customer_email}</b>. Check your inbox
                    (and spam folder) — the message is titled
                    <i> &ldquo;Your Kalki Ai Cloud workspace is live&rdquo;</i>.
                  </p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex flex-col gap-2 text-left">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Your workspace</div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-lg font-semibold text-white">{row?.tenant_slug}.{masterDomain}</div>
                      <div className="text-[12px] text-zinc-500 mt-1">Bookmark this URL — it&apos;s your admin console.</div>
                    </div>
                    <a href={loginUrl} className="text-brand-gold text-sm font-semibold hover:underline">Open →</a>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-3 items-center justify-center">
                  <GradientButton href={loginUrl || '#'} size="lg">
                    Sign in to your workspace <ArrowRight className="h-4 w-4" />
                  </GradientButton>
                  <GhostButton href="/" size="lg">Back to Kalki Ai Cloud</GhostButton>
                </div>

                <div className="text-[12px] text-zinc-500 flex items-center justify-center gap-1.5 mt-4">
                  <Mail className="h-3 w-3" />
                  Didn&apos;t receive the email? Check spam, then contact support at{' '}
                  <a href="mailto:hello@kalkiai.trade" className="text-brand-gold hover:underline">hello@kalkiai.trade</a>.
                </div>
              </>
            )}

            {status === 'timeout' && (
              <>
                <div className="mx-auto h-16 w-16 rounded-full bg-amber-400/20 border-2 border-amber-400/40 flex items-center justify-center">
                  <AlertTriangle className="h-8 w-8 text-amber-400" />
                </div>
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-amber-400 mb-3">Provisioning is taking longer than usual</div>
                  <h1 className="text-3xl md:text-4xl font-semibold text-white leading-tight">Your payment is safe.</h1>
                  <p className="text-zinc-400 md:text-lg leading-relaxed mt-4 max-w-lg mx-auto">
                    Stripe confirmed your payment, but tenant provisioning is taking longer
                    than 60 seconds. This is rare and self-heals — the credentials email
                    should still arrive within a few minutes. If not, contact support.
                  </p>
                </div>
                <div className="flex flex-col md:flex-row gap-3 items-center justify-center">
                  <GradientButton onClick={() => window.location.reload()} size="lg">Refresh status</GradientButton>
                  <GhostButton href="mailto:hello@kalkiai.trade" size="lg">Contact support</GhostButton>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="mx-auto h-16 w-16 rounded-full bg-red-400/20 border-2 border-red-400/40 flex items-center justify-center">
                  <AlertTriangle className="h-8 w-8 text-red-400" />
                </div>
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-red-400 mb-3">Something went wrong</div>
                  <h1 className="text-3xl md:text-4xl font-semibold text-white leading-tight">We couldn&apos;t verify your session.</h1>
                  <p className="text-zinc-400 md:text-lg leading-relaxed mt-4 max-w-lg mx-auto">
                    Your payment (if made) is safe with Stripe. Please contact support so we
                    can look up your session manually and finish provisioning.
                  </p>
                </div>
                <GradientButton href="mailto:hello@kalkiai.trade" size="lg">Contact support</GradientButton>
              </>
            )}
          </GlassCard>
        </Container>
      </section>
    </SiteFrame>
  )
}

export default function WlSignupSuccessPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-black text-zinc-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    }>
      <SuccessPageInner />
    </Suspense>
  )
}
